import React, {useContext, useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {Context} from "../index";
import {Chart} from "react-chartjs-2";
import {roundToSigns} from "../utils/utils";
import {Chart as ChartJS} from "chart.js";
import "chart.js/auto";

const Graph = ({scores, dates, estimates}) => {
    const {userStore} = useContext(Context);
    const maxY = Math.max(...scores);
    const minY = Math.min(...scores);

    const minLim = (minY - 0.3 >= 1 ? minY - 0.3 : 1);
    const maxLim = (maxY + 0.3 <= 5 ? maxY + 0.3 : 5);
    const minLimRef = React.useRef(minLim);
    const maxLimRef = React.useRef(maxLim);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1000);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        minLimRef.current = minLim;
        maxLimRef.current = maxLim;
    }, [scores, minLim, maxLim]);

    useEffect(() => {
        if (userStore.theme === "dark") {
            ChartJS.defaults.color = "#e0e0e0"; // общий цвет текста (подписи осей, легенда, тики)
            ChartJS.defaults.borderColor = "rgba(255,255,255,0.2)"; // линии сетки

            ChartJS.defaults.plugins.legend.labels.color = "#ffffff";
            ChartJS.defaults.plugins.tooltip.backgroundColor = "#000";
            ChartJS.defaults.plugins.tooltip.titleColor = "#ffffff";
            ChartJS.defaults.plugins.tooltip.bodyColor = "#ffffff";
        }
    }, [userStore.theme]);

    return (
        <>
            {scores.length > 1 ? <Chart
                className='graph'
                type="line"
                data={{
                    labels: dates,
                    datasets: [{
                        clip: false,
                        label: 'Средний балл',
                        data: scores,
                        backgroundColor: 'black',
                        borderWidth: isMobile ? 4 : scores.length > 60 ? 4 : 2,
                        segment: {
                            borderColor: ctx => {
                                const curr = ctx.p0.parsed.y;
                                const next = ctx.p1.parsed.y;
                                return next >= curr ? userStore.theme === "dark" ? '#00c800' : "green" : 'red'; // dart theme: return next >= curr ? '#0f0' : 'red';
                            }
                        },
                        pointBackgroundColor: ctx => {
                            let index = ctx.dataIndex;
                            if (index === 0) index = 1;
                            const prev = scores[index - 1];
                            const curr = scores[index];
                            return curr >= prev ? userStore.theme === "dark" ? '#00c800' : "green" : 'red'; // здесь тоже
                        },
                        pointBorderColor: 'transparent', // убираем границу
                        pointBorderWidth: 0,
                        pointRadius: isMobile ? scores.length > 40 ? 2 : 3.5 : scores.length > 60 ? 2 : 5,
                        pointHoverRadius: isMobile ? 5 : 7,
                    }]
                }}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: false // убираем легенду
                        },
                        tooltip: {
                            callbacks: {
                                title: (context) => {
                                    let change = roundToSigns(scores[context[0].dataIndex], 2) - roundToSigns(scores[context[0].dataIndex-1], 2);
                                    if (!change && change !== 0 && change !== 1) change = "";
                                    const round = roundToSigns(change, 2, false);
                                    let changeString = "";
                                    if (change !== "") {
                                        changeString += "(";
                                        if (change > 0) changeString += "+";
                                        changeString += `${round})`;
                                    }
                                    return `${estimates[context[0].dataIndex]} - ${context[0].label} ${changeString}`;
                                },
                                label: (context) => {
                                    const value = context.raw;
                                    const string = roundToSigns(value, 2, false);
                                    return `Средний балл: ${string} ~ ${roundToSigns(Number(string))}`;
                                }
                            }
                        }
                    },
                    scales: {
                        y: {
                            min: minLim,
                            max: maxLim,
                            ticks: {
                                color: userStore.theme === "dark" ? "#e0e0e0" : "#232323",
                                font: {
                                    size: isMobile ? 10 : 12 // 👈 размер шрифта для оси Y
                                }
                            },
                            grid: {
                                color: userStore.theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)" // цвет сетки по Y
                            }
                        },
                        x: {
                            ticks: {
                                color: userStore.theme === "dark" ? "#e0e0e0" : "#232323",
                                font: {
                                    size: isMobile ? 10 : 12 // 👈 размер шрифта для оси Y
                                }
                            },
                            grid: {
                                color: userStore.theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)" // цвет сетки по Y
                            }
                        }
                    }
                }}
                plugins={[{
                    id: 'thresholdLines',
                    beforeDatasetsDraw(chart) {
                        const minLim = minLimRef.current;
                        const maxLim = maxLimRef.current;

                        const { ctx, chartArea: { left, right }, scales: { y } } = chart;
                        const levels = [1.5, 2.5, 3.5, 4.5].filter(v => v > minLim && v < maxLim);
                        const baseColors = userStore.theme === "dark" ? ["#707070", "#ff0000", "#ff8000", "#00c800"] : ["black", "red", "orange", "green"], colors = [];
                        for (let l of levels) colors.push(baseColors[l-1.5]);

                        ctx.save();
                        ctx.setLineDash([5, 5]);
                        ctx.lineWidth = 2;

                        levels.forEach((value, idx) => {
                            ctx.strokeStyle = colors[idx];
                            const yPos = y.getPixelForValue(value);
                            ctx.beginPath();
                            ctx.moveTo(left, yPos);
                            ctx.lineTo(right, yPos);
                            ctx.stroke();
                        });

                        ctx.restore();
                    }
                }]}
            /> : "Недостаточно оценок для построения графика"}
        </>
    );
};

export default observer(Graph);